/**
 * MapArea Component - Background Map Display and Interaction
 * 
 * This component handles the display and interaction of background map images
 * on the battle canvas. It's responsible for showing dungeon maps, outdoor scenes,
 * and other background imagery that sets the scene for D&D encounters.
 * 
 * WHAT IT DOES:
 * - Displays uploaded map images as the background layer of the battle map
 * - Allows DMs to position and resize maps to fit the scene properly
 * - Provides selection and transformation handles when the Map Layer is active
 * - Supports right-click context menu for layer management and deletion
 * - Handles drag and drop for repositioning maps
 * 
 * INTERACTION RULES:
 * - Only interactive when Map Layer is active and unlocked
 * - Shows resize handles (transformer) only when selected and interactive
 * - Can be moved and resized by DM when Map Layer is selected
 * - Right-click provides context menu for layer reassignment or deletion
 * - Non-interactive when Token Layer or GM Layer is active (prevents accidental moves)
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { DEBUG_ENABLED } from '../../../utils/DebugLogger';
import { getCanvasImageCrossOriginMode } from '../../../utils/canvasImageLoader';
import { Image, Transformer, Rect } from 'react-konva';
import useImage from 'use-image';

const MapArea = ({ 
  mapImage: mapImageSrc, 
  getAssetDraggable, 
  layer = 'map',
  isSelected,
  onSelect,
  onChange,
  onDragEnd,
  onContextMenu,
  mapPosition = { x: 0, y: 0 },
  mapSize = { width: 800, height: 600 }
}) => {
  // Pre-process the map image source to ensure it's valid
  const validMapImageSrc = mapImageSrc || '';
  
  // Track whether we have a valid image to render
  const [hasValidImage, setHasValidImage] = useState(false);
  
  // Removed unused imageKey re-render mechanism; useImage already reloads on src change

  // Load image with better error handling and debugging
  const imageCrossOriginMode = getCanvasImageCrossOriginMode(validMapImageSrc);
  const [image, imageStatus] = useImage(validMapImageSrc, imageCrossOriginMode);
  
  // Validate image loading status and update hasValidImage state
  useEffect(() => {
    // Only log errors for non-default images to reduce noise
    if (imageStatus === 'failed') {
      if (mapImageSrc && !mapImageSrc.includes('default-grid')) {
        console.error('[MapArea] Failed to load image:', mapImageSrc);
      }
      // date_of_change: 2025-08-30 - Made default image failure silent
      setHasValidImage(false);
    } else if (imageStatus === 'loaded' && image) {
      setHasValidImage(true);
      // Only log successful loads with dimensions
      if (DEBUG_ENABLED) console.debug('[MapArea] Image loaded successfully:', {
        dimensions: `${image.width}x${image.height}`,
        layer
      });
    }
  }, [mapImageSrc, image, imageStatus, layer]);
  
  const shapeRef = useRef();
  const trRef = useRef();
  const dragButtonRef = useRef(null);
  
  // Determine if this map should be interactive based on layer restrictions
  const isInteractive = getAssetDraggable ? getAssetDraggable(layer) : false;
  
  // Check if we need to render a fallback grid
  // Only render fallback if we've attempted to load an image and failed or have no image source
  const renderFallbackGrid = (!hasValidImage || !mapImageSrc || imageStatus === 'failed');
  
  // Only log fallback grid rendering once when it changes
  useEffect(() => {
    if (renderFallbackGrid) {
      if (DEBUG_ENABLED) console.debug('[MapArea] Rendering fallback grid - no valid image available', {
        layer,
        imageStatus
      });
    }
  }, [renderFallbackGrid, layer, imageStatus]);
  
  // Safety checks for position and size
  const safeMapPosition = mapPosition || { x: 0, y: 0 };
  const safeMapSize = mapSize || { width: 800, height: 600 };
  
  // Handle transformer attachment when selected and interactive
  useEffect(() => {
    if (isSelected && isInteractive && trRef.current && shapeRef.current) {
      if (DEBUG_ENABLED) console.debug('[MapArea] Attaching transformer - selected and interactive');
      // Force a slight delay to ensure proper attachment
      setTimeout(() => {
        if (trRef.current && shapeRef.current) {
          trRef.current.nodes([shapeRef.current]);
          trRef.current.getLayer().batchDraw();
        }
      }, 50);
    } else if (trRef.current) {
      if (DEBUG_ENABLED) console.debug('[MapArea] Detaching transformer - not selected or interactive', { isSelected, isInteractive });
      trRef.current.nodes([]);
    }
  }, [isSelected, isInteractive, image, imageStatus]);
  
  // Handle transform end (resize)
  const handleTransformEnd = () => {
    if (!isInteractive || !onChange) {
      if (DEBUG_ENABLED) console.debug('[MapArea] Transform end ignored - not interactive or no onChange handler', { isInteractive, hasOnChange: !!onChange });
      return;
    }
    
    const node = shapeRef.current;
    if (!node) {
      console.error('[MapArea] Transform end failed - shapeRef.current is null');
      return;
    }
    
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    const newWidth = Math.max(50, node.width() * scaleX);
    const newHeight = Math.max(50, node.height() * scaleY);

    // Reset scale and update size
    node.scaleX(1);
    node.scaleY(1);
    
    if (DEBUG_ENABLED) console.debug('[MapArea] Resizing map to:', { 
      x: node.x(), 
      y: node.y(), 
      width: newWidth, 
      height: newHeight 
    });
    
    onChange({
      x: node.x(),
      y: node.y(),
      width: newWidth,
      height: newHeight,
    });
  };

  const handleMouseDown = useCallback((e) => {
    dragButtonRef.current = e?.evt?.button ?? null;
  }, []);

  const handleMouseUp = useCallback(() => {
    dragButtonRef.current = null;
  }, []);

  const handleDragStart = useCallback((e) => {
    if (dragButtonRef.current === 1) {
      e?.target?.stopDrag?.();
      return;
    }
  }, []);

  const handleMapDragEnd = useCallback((e) => {
    dragButtonRef.current = null;
    if (typeof onDragEnd === 'function') {
      onDragEnd(e);
    }
  }, [onDragEnd]);

  return (
    <React.Fragment>
      {renderFallbackGrid ? (
        // Render a fallback background when no image is available
        <Rect
          id="map-background"
          ref={shapeRef}
          x={safeMapPosition.x}
          y={safeMapPosition.y}
          width={safeMapSize.width}
          height={safeMapSize.height}
          fill="#303030"
          stroke={isSelected ? "#3388ff" : "#555555"}
          strokeWidth={isSelected ? 3 : 2}
          draggable={isInteractive}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onClick={() => {
            if (DEBUG_ENABLED) console.debug('[MapArea] Fallback grid clicked, calling onSelect', { isInteractive });
            if (onSelect) onSelect();
          }}
          onDragStart={handleDragStart}
          onDragEnd={handleMapDragEnd}
          onTransformEnd={handleTransformEnd}
          onContextMenu={onContextMenu}
        />
      ) : (
        // Render the actual map image
        <Image
          id="map-background"
          ref={shapeRef}
          image={image}
          x={safeMapPosition.x}
          y={safeMapPosition.y}
          width={safeMapSize.width}
          height={safeMapSize.height}
          stroke={isSelected ? "#3388ff" : null}
          strokeWidth={isSelected ? 3 : 0}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onClick={() => {
            if (DEBUG_ENABLED) console.debug('[MapArea] Map image clicked, calling onSelect', { isInteractive });
            if (onSelect) onSelect();
          }}
          draggable={isInteractive}
          onDragStart={handleDragStart}
          onDragEnd={handleMapDragEnd}
          onTransformEnd={handleTransformEnd}
          onContextMenu={onContextMenu}
        />
      )}

      {/* Only render transformer when map is selected and interactive */}
      {isSelected && isInteractive && (
        <Transformer
          ref={trRef}
          keepRatio={false}
          borderStroke="#3388ff"
          borderStrokeWidth={2}
          anchorStroke="#3388ff"
          anchorFill="#ffffff"
          anchorSize={10}
          boundBoxFunc={(oldBox, newBox) => {
            // Ensure minimum size
            if (newBox.width < 50 || newBox.height < 50) {
              return oldBox;
            }
            return newBox;
          }}
        />
      )}
    </React.Fragment>
  );
};

// Custom comparison function for React.memo to prevent unnecessary re-renders
const arePropsEqual = (prevProps, nextProps) => {
  // Check for significant prop changes that should trigger re-render
  if (prevProps.mapImage !== nextProps.mapImage) return false;
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  if (prevProps.layer !== nextProps.layer) return false;
  
  // Compare map position and size
  if (prevProps.mapPosition?.x !== nextProps.mapPosition?.x ||
      prevProps.mapPosition?.y !== nextProps.mapPosition?.y) return false;
  
  if (prevProps.mapSize?.width !== nextProps.mapSize?.width ||
      prevProps.mapSize?.height !== nextProps.mapSize?.height) return false;
      
  // Check if getAssetDraggable function reference changed
  // Note: This is not a deep equality check, just a reference check
  if (prevProps.getAssetDraggable !== nextProps.getAssetDraggable) return false;
  
  // If we got here, the props that matter haven't changed
  if (DEBUG_ENABLED) {
    console.debug('[MapArea] Skipping re-render - props are equivalent');
  }
  return true;
};

export default React.memo(MapArea, arePropsEqual);
